import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Moment from "react-moment";
import {
  PhoneIcon,
  VideoIcon,
  DeleteIcon,
} from "../../../svg/chat";
import { getCallHistory, deleteCall, formatCallDuration } from "../../../functions/call";
import "./style.css";

export default function CallHistory({ user, onCallUser }) {
  const [calls, setCalls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);

  useEffect(() => {
    loadCalls();
  }, []);

  const loadCalls = async (pageNum = 1) => {
    setLoading(true);
    const result = await getCallHistory(pageNum, 20, user.token);

    if (result.status === "ok") {
      if (pageNum === 1) {
        setCalls(result.calls);
      } else {
        setCalls((prev) => [...prev, ...result.calls]);
      }
      setHasMore(result.pagination.page < result.pagination.pages);
      setPage(pageNum);
    }
    setLoading(false);
  };

  const handleDeleteCall = async (callId, e) => {
    e.stopPropagation();
    if (!window.confirm("Remover esta chamada do histórico?")) return;

    const result = await deleteCall(callId, user.token);
    if (result.status === "ok") {
      setCalls((prev) => prev.filter((c) => c._id !== callId));
    }
  };

  const getCallInfo = (call) => {
    const isCaller = call.caller._id === user.id;
    const otherUser = isCaller
      ? call.participants.find((p) => p.user._id !== user.id)?.user
      : call.caller;

    return {
      user: otherUser,
      isCaller,
      isOutgoing: isCaller,
      isMissed: call.status === "missed" && !isCaller,
      isRejected: call.status === "rejected",
    };
  };

  const getCallStatusIcon = (call, info) => {
    if (info.isMissed) {
      return <span className="call_status_icon missed">
        <i className="fa fa-arrow-down"></i>
      </span>;
    }
    if (info.isRejected) {
      return <span className="call_status_icon rejected">
        <i className="fa fa-times"></i>
      </span>;
    }
    if (info.isOutgoing) {
      return <span className="call_status_icon outgoing">
        <i className="fa fa-arrow-up"></i>
      </span>;
    }
    return <span className="call_status_icon incoming">
      <i className="fa fa-arrow-down"></i>
    </span>;
  };

  const getCallStatusText = (call, info) => {
    if (info.isMissed) return "Chamada perdida";
    if (info.isRejected) return info.isCaller ? "Recusada" : "Você recusou";
    if (call.status === "ended") {
      return formatCallDuration(call.duration);
    }
    return info.isOutgoing ? "Chamada efetuada" : "Chamada recebida";
  };

  const handleCallClick = (call) => {
    const info = getCallInfo(call);
    if (onCallUser && info.user) {
      onCallUser(info.user._id, call.type);
    }
  };

  if (loading && calls.length === 0) {
    return (
      <div className="call_history_loading">
        <div className="loading_skeleton"></div>
        <div className="loading_skeleton"></div>
        <div className="loading_skeleton"></div>
      </div>
    );
  }

  if (calls.length === 0) {
    return (
      <div className="call_history_empty">
        <div className="empty_icon">
          <PhoneIcon color="var(--color-secondary)" />
        </div>
        <h3>Sem chamadas recentes</h3>
        <p>Suas chamadas de voz e vídeo aparecerão aqui</p>
      </div>
    );
  }

  return (
    <div className="call_history">
      {calls.map((call) => {
        const info = getCallInfo(call);

        return (
          <div
            key={call._id}
            className={`call_item ${info.isMissed ? "missed" : ""}`}
            onClick={() => handleCallClick(call)}
          >
            <Link
              to={`/profile/${info.user?.username}`}
              className="call_avatar"
              onClick={(e) => e.stopPropagation()}
            >
              <img
                src={info.user?.picture}
                alt={`${info.user?.first_name} ${info.user?.last_name}`}
                onError={(e) => {
                  e.target.src =
                    "https://res.cloudinary.com/dmhcnhtng/image/upload/v1643044376/avatars/default_pic_jeaybr.png";
                }}
              />
            </Link>

            <div className="call_info">
              <div className="call_header">
                <span className="call_name">
                  {info.user?.first_name} {info.user?.last_name}
                </span>
                <span className="call_time">
                  <Moment fromNow>{call.createdAt}</Moment>
                </span>
              </div>
              <div className="call_details">
                {getCallStatusIcon(call, info)}
                <span className="call_type_icon">
                  {call.type === "video" ? (
                    <VideoIcon color="var(--color-secondary)" />
                  ) : (
                    <PhoneIcon color="var(--color-secondary)" />
                  )}
                </span>
                <span className="call_status_text">
                  {getCallStatusText(call, info)}
                </span>
              </div>
            </div>

            <div className="call_actions">
              <button
                className="call_action_btn"
                title={call.type === "video" ? "Videochamada" : "Chamada de voz"}
                onClick={(e) => {
                  e.stopPropagation();
                  handleCallClick(call);
                }}
              >
                {call.type === "video" ? (
                  <VideoIcon color="#1877f2" />
                ) : (
                  <PhoneIcon color="#1877f2" />
                )}
              </button>
              <button
                className="call_action_btn delete"
                title="Remover"
                onClick={(e) => handleDeleteCall(call._id, e)}
              >
                <DeleteIcon color="var(--color-secondary)" />
              </button>
            </div>
          </div>
        );
      })}

      {hasMore && (
        <button
          className="load_more_btn"
          onClick={() => loadCalls(page + 1)}
          disabled={loading}
        >
          {loading ? "Carregando..." : "Carregar mais"}
        </button>
      )}
    </div>
  );
}
